/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-promise-executor-return */
/* eslint-disable no-param-reassign */
// eslint-disable-next-line import/no-unresolved
import { getConvertedPrice } from '@src/utils';
import { Auth } from '@aws-amplify/auth';
import { fixSlashes } from '../../../../src/utils/utilities';
import {
  DOLLAR,
  DOLLAR_CURRENCIES,
  OTHER_CURRENCIES,
  TAX_INCLUDED_CURRENCIES,
} from '../constants/currency';

import ENABLED_3DS_COUNTRIES from '../constants/enabled3DSCountries';

export const isBrowser = typeof window !== 'undefined';
export const RESELLER_ACCESS_ID = "reseller_access_id";
const USER_INFO_KEY = 'user-info';

export const isEmpty = (obj) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false;
    }
  }
  return JSON.stringify(obj) === JSON.stringify({});
};

export const setLocalStorage = (key, data) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  isBrowser && window.localStorage && window.localStorage.setItem(key, data);
};

export const getLocalStorage = (key) =>
  isBrowser && window.localStorage ? window.localStorage.getItem(key) : undefined;

export const deleteLocalStorage = (key) =>
  isBrowser && window.localStorage && window.localStorage.removeItem(key);

export const deleteSessionStorage = (key) => {
  if (key) {
    isBrowser && window.sessionStorage && window.sessionStorage.removeItem(key)
  }
}

export const setSessionStorage = (key, data) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  isBrowser && window.sessionStorage && window.sessionStorage.setItem(key, JSON.stringify(data.toLowerCase()));
};

export const getSessionStorage = (key) => isBrowser && window.sessionStorage
    ? JSON.parse(window.sessionStorage.getItem(key)) || ""
    : "";

export const getParamsFromURL = paramName => {
  // refer: https://stackoverflow.com/questions/1403888/get-escaped-url-parameter/8764051#8764051
  if (isBrowser) {
    const match = RegExp(`[?&]${  paramName  }=([^&;]+?)(&|#|;|$)`).exec(
      window.location.href
    )
    /*
     * The encodeURIComponent() function encodes a URI component.
     * It encodes the following characters: , / ? : @ & = + $ #
     */
    return match && decodeURIComponent(match[1].replace(/\+/g, "%20"))
  }
}

/**
 * This function parse URL search string and unpack it into a js object.
 *
 * It returns js object only when following conditions satisfy
 *   * It is a valid URL search string
 *   * All required fields are in the search string
 *
 * @param {string} search URL search string
 * @returns js object or null
 */
export function unpackSignedLink(location) {
  const nameMapping = {
    email: "email",
    accessid: "accessId",
    productid: "productId",
    externalid: "externalId",
    timestamp: "timestamp",
    expirydays: "expiryDays",
    guid: "guid",
    signature: "signature",
    data: "data",
  }
  const params = new URLSearchParams(location.search)

  const result = {
    hostName: location.hostname,
    path: location.pathname.replace(/\/$/, ''),
  }
  Array.from(params.keys()).forEach((param) => {
    if (nameMapping[param]) {
      result[nameMapping[param]] = decodeURIComponent(params.get(param))
    }
  });

  // check the mandatory fields
  if (
    !result.signature ||
    !result.accessId ||
    !result.timestamp ||
    !result.guid
  ) {
    return null
  }

  return result
}

// Get automation reCaptcha auth and nonce from local storage
export const getAutomationRecaptcha = () => {
  const automationRecaptcha = getLocalStorage('automation-recaptcha');

  if (!automationRecaptcha) {
    return null;
  }
  const parsedCaptcha = JSON.parse(automationRecaptcha);
  return parsedCaptcha;
};

export const checkHaveValue = (values) => Object.keys(values).some((key) => values[key]);

export const priceFormat = ({ cents, currency, addTax, toFixedNumber, taxLabel = '' }) => {
  const priceObj = {
    currency,
    price: getConvertedPrice(cents, currency, toFixedNumber),
    addTax,
    taxLabel,
  };

  return currencyFormat(priceObj);
};

export const currencyFormat = ({ currency, price, addTax, taxLabel = '' }) => {
  // first, check if the price is .10, .20, .30, .40, .50, .60, .70, .80, or .90, then ensure there are 2 decimal places
  const formattedPrice = Number(price).toFixed(2);
  if (!Number.isInteger(Number(price)) && formattedPrice.slice(-1) === '0') {
    price = formattedPrice;
  }
  // next, check if tax is included in this currency and output the appropriate text
  // todo, make this a backend thing instead of frontend
  // If the currency is not in the TAX_INCLUDED_CURRENCIES, then the text is tax excluded
  const isTaxExcluded = TAX_INCLUDED_CURRENCIES[currency] && addTax;
  let result = isTaxExcluded ? `$${price} ${taxLabel}` : `$${price}`;
  if (DOLLAR_CURRENCIES[currency]) {
    result = isTaxExcluded
      ? `${DOLLAR}${price} ${DOLLAR_CURRENCIES[currency]} ${taxLabel}`
      : `${DOLLAR}${price} ${DOLLAR_CURRENCIES[currency]}`;
  } else if (OTHER_CURRENCIES[currency]) {
    result = isTaxExcluded
      ? `${OTHER_CURRENCIES[currency]}${price} ${taxLabel}`
      : `${OTHER_CURRENCIES[currency]}${price}`;
  }
  return result;
};

export const firstPaymentDateFormat = (trialDays, lang) => {
  const date = new Date().setDate(new Date().getDate() + trialDays);
  const options = { month: 'long', day: '2-digit' };
  const firstPaymentDate = new Date(date).toLocaleDateString(lang, options);
  const shortMonth = firstPaymentDate.split(' ')[1].slice(0, 3);
  return `${firstPaymentDate.split(' ')[0]} ${shortMonth}`;
};

export const enabled3DSBillingAddress = (addressCountry) =>
  ENABLED_3DS_COUNTRIES.includes(addressCountry?.toLowerCase()) === true;

const isLargerThanOneMonthSubscription = (subscription) => {
  const { interval, interval_unit } = subscription || {};
  return (interval > 1 && interval_unit === 'month') || interval_unit === 'year';
};

const isTwelveMonthSubscription = (subscription) => {
  const { interval, interval_unit } = subscription || {};
  return interval === 12 && interval_unit === 'month';
};

export const checkMultiMonthRolloverCountry = (rolloverCountries, userCountry) =>
  rolloverCountries?.threeMonth?.includes(userCountry?.toLowerCase());

export const checkIsTwelveMonthRolloverCountry = (rolloverCountries, userCountry) =>
  rolloverCountries?.twelveMonth?.includes(userCountry?.toLowerCase());

export const allowRolloverPreferences = (userCountry, subscription, rolloverCountries) => {
  if (rolloverCountries) {
    const isMultiMonthRollover =
      isLargerThanOneMonthSubscription(subscription) &&
      checkMultiMonthRolloverCountry(rolloverCountries, userCountry);

    const isTwelveMonthRollover =
      isTwelveMonthSubscription(subscription) &&
      checkIsTwelveMonthRolloverCountry(rolloverCountries, userCountry);

    return isMultiMonthRollover || isTwelveMonthRollover;
  }
};

export const defaultToMonthly = ({
  productHandle,
  countryCode,
  productPricePoint,
  rolloverValue,
  rolloverCountries,
}) => {
  const isAllowRollover = allowRolloverPreferences(
    countryCode,
    productPricePoint,
    rolloverCountries
  );
  let result = 'false';
  if (isAllowRollover) {
    result = rolloverValue && rolloverValue === 'monthly' ? 'true' : 'false';
  } else {
    if (countryCode === 'nl') {
      result = productHandle.includes('12monthly') ? 'true' : 'false';
    }
    if (countryCode === 'dk') {
      result = productHandle.includes('6monthly') ? 'true' : 'false';
    }
    if (countryCode === 'de' && productHandle.includes('6monthly')) {
      result = 'false';
    }
  }
  return result;
};

export const addErrorInDatadogRum = (error) => {
  if (isBrowser && window.datadogRum) {
    const userInfo = JSON.parse(getLocalStorage(USER_INFO_KEY) || 'null');
    window.datadogRum.addError(error, {
      context: {
        userId: userInfo?.userId,
      },
    });
  }
};

export const isOptInNewsletterCountry = () => {
  const userInfo = getLocalStorage(USER_INFO_KEY);
  const userCountry = userInfo ? userInfo?.addressCountry : null;
  const optInNewsletterCountries = ['us'];
  return userCountry
    ? optInNewsletterCountries.includes(userCountry.toLowerCase())
    : false
}

const generateAffiliateUrl = (discountInfo) => {
  const { affiliateId, offerId, transactionId, voucherCode } = discountInfo?.data || {};
  const queryParams = [];

  if (affiliateId) queryParams.push(`aff_id=${affiliateId}`);
  if (offerId) queryParams.push(`offer_id=${offerId}`);
  if (transactionId) queryParams.push(`transaction_id=${transactionId}`);
  if (voucherCode) queryParams.push(`voucher_code=${voucherCode}`);

  const queryString = queryParams.length > 0 ? `&${queryParams.join('&')}` : '';

  return queryString;
};

// We are getting winback info from BE now
export const getLoginUrl = (discountInfo, lang, isPartnerSignUp, tiering) => {
  const currentLang = lang !== 'en-nz' ? `/${lang}` : '';
  // note the / prefixes a non-English language in currentLang

  if (isPartnerSignUp) {
    return `/login${currentLang}`;
  }

  let path = '';

  if (discountInfo) {
    if (discountInfo?.type === 'promotionCode') {
      path = `/${discountInfo?.data?.promotionCode}`;
    }
    if (discountInfo?.type === 'affiliate') {
      path = `/affiliate`;
    }
  }

  const selectPlanPage = fixSlashes(`/signup/select-plan/${path}/${currentLang}`);
  let loginUrl = `/login${currentLang}?redirect_to=${encodeURIComponent(selectPlanPage)}`;
  if (tiering !== null) {
    loginUrl += `&tiering=${tiering}`;
  }
  // if the user came from a promotional link or an affiliate link, and they are an old/expired customer, retain the promo code in the login url so it stays in LocalStorage
  if (discountInfo?.type === 'promotionCode') {
    loginUrl += `&promo=${discountInfo?.data?.promotionCode}`;
  } else if (discountInfo?.type === 'affiliate') {
    const affiliateUrl = generateAffiliateUrl(discountInfo);
    loginUrl += affiliateUrl;
  }

  return loginUrl;
};

export const waitForElementToLoad = (querySelector, timeout) =>
  new Promise((resolve, reject) => {
    let timer = false;

    if (document.querySelectorAll(querySelector).length) return resolve();
    const observer = new MutationObserver(() => {
      if (document.querySelectorAll(querySelector).length) {
        observer.disconnect();
        if (timer !== false) clearTimeout(timer);
        return resolve();
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    if (timeout)
      timer = setTimeout(() => {
        observer.disconnect();
        reject();
      }, timeout);
  });

export const getDiscountInfo = () => JSON.parse(getLocalStorage('discountInfo') || null);

/**
 * Parse JWT token to extract custom:entitlements claim
 */
const parseJwtToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(base64));
  } catch (error) {
    return null;
  }
};

/**
 * Check if LMOD exists in the entitlements claim
 */
const hasLmodEntitlement = (jwtToken) => {
  const decodedToken = parseJwtToken(jwtToken);
  if (!decodedToken || !decodedToken['custom:entitlements']) {
    return false;
  }

  try {
    const entitlements = JSON.parse(decodedToken['custom:entitlements']);
    return !!entitlements.LMOD;
  } catch (error) {
    return false;
  }
};

/**
 * Refresh JWT token with retry logic
 * Retries with increasing intervals if LMOD entitlement is not present
 * Gives up after 30 attempts (~30 seconds)
 *
 * @returns {Promise<string>} The refreshed JWT token
 * @throws {Error} If refresh fails or LMOD entitlement is not found after max attempts
 */
export const refreshTokenWithRetry = async () => {
  const MAX_ATTEMPTS = 30;
  const RETRY_INTERVAL_MS = 1000;

  const attemptRefresh = async (attemptNumber) => {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const currentSession = await Auth.currentSession();

    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(currentSession.getRefreshToken(), (err, session) => {
        if (err) {
          reject(err);
          return;
        }

        const jwtToken = session?.idToken?.jwtToken;

        // Check if LMOD entitlement exists
        if (hasLmodEntitlement(jwtToken)) {
          // Store the token and resolve
          localStorage.setItem('jwtToken', jwtToken);
          resolve(jwtToken);
        } else if (attemptNumber < MAX_ATTEMPTS) {
          // LMOD not found, retry after delay
          setTimeout(() => {
            attemptRefresh(attemptNumber + 1)
              .then(resolve)
              .catch(reject);
          }, RETRY_INTERVAL_MS);
        } else {
          // Max attempts reached without finding LMOD
          reject(new Error('LMOD entitlement not found after maximum retry attempts'));
        }
      });
    });
  };

  return attemptRefresh(1);
};
